import { z } from 'zod';

/** Which of a selected file's two views is on screen. */
export const FILE_VIEWS = ['preview', 'history'] as const;
export type FileView = (typeof FILE_VIEWS)[number];

/**
 * Search parameters for the workspace file browser route.
 *
 * Search parameters are user-editable text, so an unrecognised value falls
 * back to the default rather than failing the route. `path`/`file` are
 * free-form strings naming a folder/file within the workspace's file
 * storage — no format to validate beyond "string" — and `view` says whether
 * the selected file is showing its content or its change log.
 *
 * `view` replaced a `historyOffset` page number (FR-015/FR-F015): what is
 * worth sharing or bookmarking is "I am looking at this file's history", not
 * "page three of it". How far back the reader has scrolled is cache state now,
 * and an old link carrying `historyOffset` simply drops it.
 *
 * Sort key/direction are deliberately not included here: the spec's
 * FR-015/FR-F015 only requires folder/file/view navigation to be
 * URL-addressable, not sort state (research.md R10).
 */
export const filesSearchSchema = z.object({
  path: z.string().catch('').default(''),
  file: z.string().optional().catch(undefined),
  view: z.enum(FILE_VIEWS).catch('preview').default('preview'),
});

export type FilesSearch = z.infer<typeof filesSearchSchema>;
